import React, { useEffect, useState } from "react";
import {
  Box,
  Alert,
  Typography,
  FormControl,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Button,
} from "@mui/material";
import SubjectCleaningArea from "./SubjectCleaningArea";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  selectedSubjectAtom,
  currentSessionAtom,
  intraIdReviewStatusFilterValueAtom,
} from "../state";
import Spinner from "./Spinner";
import SubjectsItemSelector from "./SubjectsItemSelector";
import { FINALIZED_REVIEW_STATUS, NEEDS_REVIEW_STATUS, PRE_CLEAN_REVIEW_STATUS } from "../constants";
import { useSessionApiClient } from "../api/sessions";
import ImageSizeSlider from "./ImageSizeSlider";

const renderContent = (
  subjects,
  selectedSubject,
  setSelectedSubject,
  sessionId,
  fetchingSubjects,
  reviewStatusFilterValue
) => {
  if (subjects.length === 0 && !fetchingSubjects) {
    return (
      <Box sx={{ margin: "1em" }}>
        <Alert severity="warning">
          <Typography>
            No subjects with review status {reviewStatusFilterValue} were
            found.
          </Typography>
        </Alert>
      </Box>
    );
  }
  return (
    <Grid container spacing={1} sx={{ margin: 0 }}>
      <Grid item xs={1}>
        <React.Suspense fallback={<div>Fetching subjects ...</div>}>
          <SubjectsItemSelector
            selectedItem={selectedSubject}
            setSelectedItem={setSelectedSubject}
            itemIdProp={"name"}
            renderItem={(subject) => subject.name}
            subjects={subjects}
            fetchingSubjects={fetchingSubjects}
            reviewStatusFilterValue={reviewStatusFilterValue}
          ></SubjectsItemSelector>
        </React.Suspense>
      </Grid>
      <Grid item xs={11} >
        <Paper elevation={0}>
          <React.Suspense
            fallback={<Spinner message={"Loading subject images ..."} />}
          >
            <SubjectCleaningArea
              subject={selectedSubject}
              sessionId={sessionId}
              reviewStatus={reviewStatusFilterValue}
              subjects={subjects}
            />
          </React.Suspense>
        </Paper>
      </Grid>
    </Grid>
  );
}

const IntraIdCleaning = ({ sessionId }) => {
  const currentSession = useRecoilValue(currentSessionAtom);
  const [subjects, setSubjects] = useState([]);
  const apiClient = useSessionApiClient();
  const [fetchingSubjects, setFetchingSubjects] = useState(true);
  const [selectedSubject, setSelectedSubject] =
    useRecoilState(selectedSubjectAtom);
  const [reviewStatusFilterValue, setReviewStatusFilterValue] =
    useRecoilState(intraIdReviewStatusFilterValueAtom);
  const onSubjectReviewStatusFilterChange = (e, value) => {
    setReviewStatusFilterValue(value);
  }

  const updateSubjectReviewStatus = async () => {
    const newReviewStatus = (reviewStatusFilterValue === NEEDS_REVIEW_STATUS) ? FINALIZED_REVIEW_STATUS : NEEDS_REVIEW_STATUS;
    await apiClient.updateSubjectReviewStatus(sessionId, selectedSubject.name, newReviewStatus);
    
    if (newReviewStatus === NEEDS_REVIEW_STATUS) {
      // take the user to that subject
      setReviewStatusFilterValue(NEEDS_REVIEW_STATUS);
      setSelectedSubject({ name: selectedSubject.name});
    } else {
      // continue editing the remaining subjects
      setSubjects(
        subjects.filter(
          (subject) => subject.name !== selectedSubject.name
        )
      );
    }
  }

  useEffect(() => {
    // Reload subject list
    setFetchingSubjects(true);
    const fetchSubjects = async () => {
      let subjects = [];

      if (apiClient && currentSession?.id) {
        subjects =
          await apiClient.getSubjectNamesFromSessionId(
            currentSession.id,
            reviewStatusFilterValue
          );
      }
      setSubjects(subjects);
    };

    fetchSubjects().finally(() => {
      setFetchingSubjects(false);
    });
  }, [reviewStatusFilterValue]);

  return (
    <Box sx={{ flexGrow: 1, margin: "1em" }}>
      <Box sx={{ textAlign: "center" }}>
        <Grid container spacing={1} sx={{ margin: 0}} alignItems="center">
          <Grid item xs={4} display="flex" justifyContent="flex-start">
            <FormControl spacing={1}>
              <RadioGroup
                row
                aria-labelledby="review-status-radio-buttons-group-label"
                defaultValue={NEEDS_REVIEW_STATUS}
                name="review-status-radio-buttons-group"
                onChange={onSubjectReviewStatusFilterChange}
              >
                <FormControlLabel
                  value={NEEDS_REVIEW_STATUS}
                  control={<Radio />}
                  label="Need Review"
                  // radio buttons reflect the changes if we change review status automatically 
                  // (when a user clicks edit on a finalized subject)
                  checked={reviewStatusFilterValue === NEEDS_REVIEW_STATUS}
                />
                <FormControlLabel
                  value={PRE_CLEAN_REVIEW_STATUS}
                  control={<Radio />}
                  label="Pre-Clean (Read-Only)"
                  checked={reviewStatusFilterValue === PRE_CLEAN_REVIEW_STATUS}
                />
                <FormControlLabel
                  value={FINALIZED_REVIEW_STATUS}
                  control={<Radio />}
                  label="Finalized (Read-Only)"
                  checked={reviewStatusFilterValue === FINALIZED_REVIEW_STATUS}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
          {subjects.length > 0 && (
            <Button
              variant={"contained"}
              onClick={updateSubjectReviewStatus} 
              sx={{ margin: "1em" }}
            >
              {reviewStatusFilterValue === NEEDS_REVIEW_STATUS ? 'Finalize Subject' : 'Edit Subject'} {selectedSubject.name}
            </Button>
          )}
          </Grid>
          {(subjects.length > 0) &&
           <Grid item xs={4} sx={{ gridColumn: '12'}}>
            <ImageSizeSlider/>
          </Grid>
          }
        </Grid>
      </Box>
      {renderContent(
        subjects,
        selectedSubject,
        setSelectedSubject,
        sessionId,
        fetchingSubjects,
        reviewStatusFilterValue
      )}
    </Box>
  );

};

export default IntraIdCleaning;
